'use client';

import { ReactNode, useCallback, useEffect, useState } from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import AutoHeight from 'embla-carousel-auto-height';
import { carouselWrapperVariants } from './CarouselWrapper.styles';
import SvgIcon from '@/helpers/SvgIcon/SvgIcon';
import { cn } from 'tailwind-variants';
import { trackAdobeClick } from '@/utils/adobe/click';
import { useCarouselAutoHeight } from '@/lib/hooks/useCarouselAutoHeight';

export type EmblaCarouselType = UseEmblaCarouselType[1];

export interface CarouselWrapperProps {
  /** Content slides to render */
  children: ReactNode;
  /** Show dot indicators */
  showDots?: boolean;
  /** Enable autoplay */
  autoplay?: boolean;
  /** Autoplay delay in ms */
  autoplayDelay?: number;
  /** Enable infinite looping */
  loop?: boolean;
  /** Slides to scroll per interaction; 'auto' scrolls by the number of slides currently in view */
  slidesToScroll?: number | 'auto';
  /** Align slides: start, center, or end */
  align?: 'start' | 'center' | 'end';
  /** Enable fade transition instead of slide */
  fade?: boolean;
  /** Collapse the viewport to the height of the slide(s) in view instead of the tallest slide */
  autoHeight?: boolean;
  /** Thumbnail content rendered per slide — enables the thumbnail strip */
  thumbnails?: ReactNode[];
  /** Number of thumbnails to show at once */
  visibleThumbnails?: number;
  /** Additional class for the carousel wrapper */
  className?: string;
  /** Additional class for the viewport */
  viewportClassName?: string;
  /** Additional class for the slide container */
  containerClassName?: string;
  /** Additional class for the thumbnail strip wrapper */
  thumbsClassName?: string;
  /** Accessible label for the carousel region */
  ariaLabel?: string;
  /** Show prev/next arrows alongside dots (only when available) */
  showPaginationArrows?: boolean;
  /** Where to render the prev/next arrows: inline with the dots (default) or on the sides of the viewport */
  arrowLayout?: 'inline' | 'sides' | 'outer-sides';
  /** Additional class for the prev side arrow — only applies when arrowLayout='sides' */
  prevArrowClassName?: string;
  /** Additional class for the next side arrow — only applies when arrowLayout='sides' */
  nextArrowClassName?: string;
  /** Additional class for the controls wrapper (the dots row) */
  controlsClassName?: string;
  /** Additional class for each pagination dot (applies to inactive) */
  dotClassName?: string;
  /** Additional class for each pagination dot (applies to active) */
  dotActiveClassName?: string;
  /** Additional class for the autoplay toggle button */
  autoplayButtonClassName?: string;
  /** Callback fired when the selected slide changes */
  onSlideChange?: (index: number) => void;
  /** Enable carousel style based on the container width */
  isContainerBleedCarousel?: boolean;
  /** When true, stops the autoplay plugin without resetting the user's play/pause preference */
  pauseAutoplay?: boolean;
}

export const CarouselWrapper = ({
  children,
  showDots = true,
  autoplay = false,
  autoplayDelay = 4000,
  loop = false,
  slidesToScroll = 1,
  align = 'start',
  fade = false,
  autoHeight = false,
  thumbnails,
  visibleThumbnails = 3,
  className,
  viewportClassName,
  containerClassName,
  thumbsClassName,
  ariaLabel = 'Carousel',
  showPaginationArrows = false,
  arrowLayout = 'inline',
  prevArrowClassName,
  nextArrowClassName,
  controlsClassName,
  dotClassName,
  dotActiveClassName,
  autoplayButtonClassName,
  onSlideChange,
  isContainerBleedCarousel,
  pauseAutoplay = false,
}: CarouselWrapperProps) => {
  // Build plugin list
  const plugins = [
    ...(fade ? [Fade()] : []),
    ...(autoHeight ? [AutoHeight()] : []),
    ...(autoplay ? [Autoplay({ delay: autoplayDelay, stopOnInteraction: true })] : []),
  ];

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop, slidesToScroll, align }, plugins);

  // Thumbnail carousel instance (no loop, scroll 1, no plugins)
  const [thumbRef, thumbApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    align: 'start',
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  // Reflects the autoplay plugin's actual running state — drives the button icon/aria-label
  const [autoplayActive, setAutoplayActive] = useState(autoplay);

  // Keep the auto-height plugin's measurements fresh when a slide reflows vertically
  useCarouselAutoHeight(emblaApi, autoHeight);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaApi || !thumbApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi, thumbApi]
  );

  const toggleAutoplay = useCallback(() => {
    const autoplayPlugin = emblaApi?.plugins()?.autoplay as
      | { isPlaying: () => boolean; stop: () => void; play: () => void }
      | undefined;
    if (!autoplayPlugin) return;

    // Click intent is driven by the visible state (autoplayActive), not the stored preference
    if (autoplayActive) {
      autoplayPlugin.stop();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      // Only start the plugin if nothing is currently blocking it (e.g. video playback)
      if (!pauseAutoplay) autoplayPlugin.play();
    }
  }, [emblaApi, autoplayActive, pauseAutoplay]);

  // Sync autoplayActive with the plugin's actual state, whatever triggered the change
  useEffect(() => {
    if (!emblaApi || !autoplay) return;
    const onAutoplayPlay = () => setAutoplayActive(true);
    const onAutoplayStop = () => setAutoplayActive(false);
    emblaApi.on('autoplay:play', onAutoplayPlay);
    emblaApi.on('autoplay:stop', onAutoplayStop);
    return () => {
      emblaApi.off('autoplay:play', onAutoplayPlay);
      emblaApi.off('autoplay:stop', onAutoplayStop);
    };
  }, [emblaApi, autoplay]);

  // Stop or resume the autoplay plugin when the external pauseAutoplay flag changes
  useEffect(() => {
    if (!emblaApi || !autoplay) return;
    const autoplayPlugin = emblaApi.plugins()?.autoplay as
      | { isPlaying: () => boolean; stop: () => void; play: () => void }
      | undefined;
    if (!autoplayPlugin) return;
    if (pauseAutoplay) {
      autoplayPlugin.stop();
    } else if (isPlaying) {
      autoplayPlugin.play();
    }
  }, [emblaApi, autoplay, pauseAutoplay, isPlaying]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index);
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    thumbApi?.scrollTo(index);
    onSlideChange?.(index);
  }, [emblaApi, thumbApi, onSlideChange]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const {
    base,
    viewport,
    container,
    fadeContainer,
    controlsWrapper,
    dotButton,
    dotActive,
    autoplayButtonWrapper,
    autoplayButton,
    paginationArrow,
    arrowButton,
    arrowPrev,
    arrowNext,
    thumbsViewport,
    thumbsContainer,
    thumbButton,
    thumbButtonSelected,
  } = carouselWrapperVariants({
    hasThumbnails: thumbnails && thumbnails?.length > 0,
    isContainerBleedCarousel,
    autoHeight,
  });

  const GAP_SIZE_PX = 32; // gap-8 in pixels
  const effectiveVisible = Math.min(thumbnails?.length ?? visibleThumbnails, visibleThumbnails);
  const totalGapWidth = (effectiveVisible - 1) * GAP_SIZE_PX;

  const thumbButtonStyle = {
    minWidth: `calc((100% - ${totalGapWidth}px) / ${effectiveVisible})`,
  };

  const hasThumbnails = thumbnails && thumbnails.length > 0;

  const handleMouseEnter = useCallback(() => {
    if (!autoplay || !isPlaying || !emblaApi) return;
    const autoplayPlugin = emblaApi.plugins()?.autoplay as
      | { isPlaying: () => boolean; stop: () => void; play: () => void }
      | undefined;
    if (autoplayPlugin?.isPlaying()) {
      autoplayPlugin.stop();
    }
  }, [autoplay, isPlaying, emblaApi]);

  const handleMouseLeave = useCallback(() => {
    if (!autoplay || !isPlaying || !emblaApi || pauseAutoplay) return;
    const autoplayPlugin = emblaApi.plugins()?.autoplay as
      | { isPlaying: () => boolean; stop: () => void; play: () => void }
      | undefined;
    if (!autoplayPlugin?.isPlaying()) {
      autoplayPlugin?.play();
    }
  }, [autoplay, isPlaying, emblaApi, pauseAutoplay]);

  // Navigate slides with arrow keys when focus is anywhere inside the carousel
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  return (
    <div
      className={base({ class: className })}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {/* Main carousel viewport — hover pause is scoped here so controls/thumbnails don't trigger it */}
      <div
        className={viewport({ class: viewportClassName })}
        ref={emblaRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={
            fade
              ? fadeContainer({ class: containerClassName })
              : container({ class: containerClassName })
          }
        >
          {children}
        </div>
      </div>

      {/* Side-positioned prev/next arrows (vertically centered against the viewport) */}
      {showPaginationArrows && arrowLayout === 'sides' && (
        <>
          <button
            onClick={(e) => {
              trackAdobeClick({ clickName: `${ariaLabel}: previous slide` }, e ?? null);
              scrollPrev();
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            disabled={!loop && !canScrollPrev}
            aria-label="Previous slide"
            type="button"
            className={cn(arrowButton(), arrowPrev(), prevArrowClassName)}
            suppressHydrationWarning
          >
            <i className="fa-light fa-2x fa-chevron-left"></i>
          </button>
          <button
            onClick={(e) => {
              trackAdobeClick({ clickName: `${ariaLabel}: next slide` }, e ?? null);
              scrollNext();
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            disabled={!loop && !canScrollNext}
            aria-label="Next slide"
            type="button"
            className={cn(arrowButton(), arrowNext(), nextArrowClassName)}
            suppressHydrationWarning
          >
            <i className="fa-light fa-2x fa-chevron-right"></i>
          </button>
        </>
      )}

      {/* Thumbnail strip */}
      {hasThumbnails && (
        <div className={thumbsViewport({ class: thumbsClassName })} ref={thumbRef}>
          <div className={thumbsContainer()}>
            {thumbnails.map((thumb, index) => (
              <button
                key={index}
                className={cn(thumbButton(), index === selectedIndex && thumbButtonSelected())}
                style={thumbButtonStyle}
                onClick={(e) => {
                  trackAdobeClick(
                    {
                      clickName: `${ariaLabel}: thumbnail ${index + 1}`,
                      clickType: 'imagevideocarousel-thumbnail',
                    },
                    e
                  );
                  onThumbClick(index);
                }}
                onKeyDown={(e) => e.key === 'Enter' && onThumbClick(index)}
                aria-current={index === selectedIndex ? 'true' : undefined}
                type="button"
              >
                {thumb}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dot indicators & autoplay toggle — hovering the controls pauses autoplay (play/pause button excluded below) */}
      {(showDots || autoplay || (showPaginationArrows && arrowLayout === 'inline')) && (
        <div
          className={controlsWrapper({ class: controlsClassName })}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {showPaginationArrows && arrowLayout === 'inline' && (
            <button
              onClick={(e) => {
                trackAdobeClick(
                  {
                    clickName: `${ariaLabel}: previous slide`,
                  },
                  e ?? null
                );
                scrollPrev();
              }}
              disabled={!loop && !canScrollPrev}
              aria-label="Previous slide"
              type="button"
              className={paginationArrow()}
              suppressHydrationWarning
            >
              <SvgIcon icon="chevron-left" viewBox="0 0 7 13" />
            </button>
          )}

          {showDots &&
            scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={cn(
                  cn(dotButton(), dotClassName),
                  index === selectedIndex && cn(dotActive(), dotActiveClassName)
                )}
                onClick={(e) => {
                  trackAdobeClick({ clickName: `${ariaLabel}: slide ${index + 1}` }, e ?? null);
                  scrollTo(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={index === selectedIndex ? 'true' : undefined}
                type="button"
              />
            ))}

          {showPaginationArrows && arrowLayout === 'inline' && (
            <button
              onClick={(e) => {
                trackAdobeClick(
                  {
                    clickName: `${ariaLabel}: next slide`,
                  },
                  e ?? null
                );
                scrollNext();
              }}
              disabled={!loop && !canScrollNext}
              aria-label="Next slide"
              type="button"
              className={paginationArrow()}
              suppressHydrationWarning
            >
              <SvgIcon icon="chevron-right" viewBox="0 0 7 13" />
            </button>
          )}

          {autoplay && (
            <div className={autoplayButtonWrapper()}>
              <button
                className={cn(autoplayButton(), autoplayButtonClassName)}
                // Invert the wrapper's hover-pause: the play/pause button must not autopause on hover
                onMouseEnter={handleMouseLeave}
                onMouseLeave={handleMouseEnter}
                onClick={(e) => {
                  trackAdobeClick(
                    {
                      clickName: `${ariaLabel}: ${autoplayActive ? 'pause' : 'play'} autoplay`,
                    },
                    e ?? null
                  );
                  toggleAutoplay();
                }}
                aria-label={autoplayActive ? 'Pause autoplay' : 'Start autoplay'}
                type="button"
              >
                <i className={cn('fa-solid', autoplayActive ? 'fa-pause' : 'fa-play')}></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Positional metadata describing a slide's place within its carousel */
export interface SlideMeta {
  index: number; // zero-based position of this slide within the carousel
  total: number; // total number of rendered slides
}

export interface CarouselSlideProps {
  children: ReactNode;
  className?: string;
  /** When true, applies fade-compatible positioning */
  fade?: boolean;
  /** Positional metadata for this slide within the carousel */
  slideMeta?: SlideMeta;
}

export const CarouselSlide = ({
  children,
  className,
  fade = false,
  slideMeta,
}: CarouselSlideProps) => {
  const { slide, fadeSlide: fadeSlideStyle } = carouselWrapperVariants();
  // Announce the slide's position (e.g. "1 of 5") to assistive tech when metadata is provided
  const slideLabel = slideMeta ? `${slideMeta.index + 1} of ${slideMeta.total}` : 'group-slide';
  return (
    <div
      className={fade ? fadeSlideStyle({ class: className }) : slide({ class: className })}
      role="group"
      aria-roledescription="slide"
      aria-label={slideLabel}
    >
      {children}
    </div>
  );
};
